export const WEB_PLUGIN_CONTRACT_VERSION = '1.0';

export type WebPluginSurface =
  | 'None'
  | 'ProjectList'
  | 'ReceiptList'
  | 'BeforeSubmission'
  | 'Submitted';

export const WEB_PLUGIN_NAVIGATION_ICONS = ['check', 'grid', 'list', 'upload'] as const;
export type WebPluginNavigationIcon = typeof WEB_PLUGIN_NAVIGATION_ICONS[number];

export interface WebPluginNavigationDescriptor {
  label: string;
  icon: WebPluginNavigationIcon;
  order: number;
  requiresAuthentication: boolean;
}

export interface WebPluginPageDescriptor {
  id: string;
  title: string;
  route: string;
  surface: WebPluginSurface;
  navigation?: WebPluginNavigationDescriptor;
}

export interface WebPluginDescriptor {
  applicationId: string;
  id: string;
  version: string;
  displayName: string;
  priority: number;
  entryUrl: string;
  entryHash: string;
  publicConfiguration?: unknown;
  pages: WebPluginPageDescriptor[];
}

export interface PluginProblem {
  code: string;
  message: string;
  applicationId?: string;
  pluginId?: string;
}

export interface WebPluginCatalogue {
  contractVersion: string;
  revision: string;
  plugins: WebPluginDescriptor[];
  problems: PluginProblem[];
}

export type PluginPageDecision =
  | { kind: 'unchanged' }
  | { kind: 'standard' }
  | { kind: 'plugin'; pageId: string };

export type PluginApiInvoker = <TResponse = unknown>(
  operation: string,
  request: unknown,
  cancellation?: AbortSignal,
) => Promise<TResponse>;

export type PluginPageCompletion =
  | { action: 'ShowStandardPage' }
  | { action: 'CancelHostAction' }
  | { action: 'ContinueHostAction' };

export interface PluginPageLoadContext<TData = unknown> {
  readonly pluginId: string;
  readonly pluginVersion: string;
  readonly priority: number;
  readonly correlationId: string;
  readonly configurationRevision: string;
  readonly publicConfiguration?: unknown;
  readonly signal: AbortSignal;
  readonly invokeApi: PluginApiInvoker;
  readonly log: (level: 'debug' | 'information' | 'warning' | 'error', event: string) => void;
  readonly surface: WebPluginSurface;
  readonly data: TData;
  /** Always false on entry. A previous plugin's result flag is never carried forward. */
  readonly dataUpdated: false;
  readonly pageDecision: PluginPageDecision;
  readonly lastPluginShowedPage?: string;
}

export interface PluginExecutionResult<TData = unknown> {
  outcome?: 'success' | 'error';
  dataUpdated?: boolean;
  data?: TData;
  pageDecision?: PluginPageDecision;
  abortPluginChain?: boolean;
  errorCode?: string;
}

export interface PluginMountContext<TData = unknown> {
  readonly element: HTMLElement;
  readonly pluginId: string;
  readonly pluginVersion: string;
  readonly priority: number;
  readonly correlationId: string;
  readonly configurationRevision: string;
  readonly pageId: string;
  readonly surface: WebPluginSurface;
  readonly data: TData;
  readonly publicConfiguration?: unknown;
  readonly signal: AbortSignal;
  readonly invokeApi: PluginApiInvoker;
  readonly completePage: (completion: PluginPageCompletion) => void;
  readonly navigate: (path: string) => void;
  readonly log: (level: 'debug' | 'information' | 'warning' | 'error', event: string) => void;
}

export interface FormicWebPlugin<TData = unknown> {
  id: string;
  version: string;
  onPageLoad?: (
    context: PluginPageLoadContext<TData>,
  ) => PluginExecutionResult<TData> | Promise<PluginExecutionResult<TData>>;
  mount: (context: PluginMountContext<TData>) => void | (() => void) | Promise<void | (() => void)>;
}

export interface FormicWebPluginRegistrationApi {
  readonly contractVersion: typeof WEB_PLUGIN_CONTRACT_VERSION;
  register: <TData = unknown>(plugin: FormicWebPlugin<TData>) => void;
}

export interface PluginPipelineResult<TData = unknown> {
  data: TData;
  dataUpdated: boolean;
  pageDecision: PluginPageDecision;
  lastPluginShowedPage?: string;
  abortedBy?: string;
}
